import {
  DATE_FORMATS,
  DATE_FORMAT_LABELS,
  dateFormatFromConfig,
  encodeDateCellValue,
  formatDateValue,
  parseDateInput,
  parseDatePayload,
  type DateFormat,
} from '@/lib/databases/values';
import {
  buildMonthGrid,
  monthLabel,
  shiftMonth,
} from '@/lib/databases/calendar-grid';
import { createElement, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Pressable, Text } from 'react-native';

import type { BlockPropertyRecord } from '@/lib/databases/types';
import type { DatabaseColumnDefinition } from '@/lib/databases/types';

import { CellPopoverPortal } from './CellPopoverPortal.web';

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function yearMonthFromIso(iso: string | null): { year: number; month: number } {
  const fallback = { year: new Date().getFullYear(), month: new Date().getMonth() };
  if (!iso) {
    return fallback;
  }
  const [y, m] = iso.split('-').map(Number);
  if (!y || !m) {
    return fallback;
  }
  return { year: y, month: m - 1 };
}

type DateCellEditorProps = {
  column: DatabaseColumnDefinition;
  property: BlockPropertyRecord | undefined;
  disabled?: boolean;
  saving?: boolean;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onSave: (value: Record<string, unknown> | null) => Promise<void>;
  onDateFormatChange?: (format: DateFormat) => void;
  variant?: 'table' | 'panel';
};

export function DateCellEditor({
  column,
  property,
  disabled,
  saving,
  isOpen,
  onOpen,
  onClose,
  onSave,
  onDateFormatChange,
  variant = 'table',
}: DateCellEditorProps) {
  const configFormat = dateFormatFromConfig(column.config);
  const [displayFormat, setDisplayFormat] = useState<DateFormat>(configFormat);
  const parsed = parseDatePayload(
    property?.value && typeof property.value === 'object'
      ? (property.value as { t: string; v?: unknown; end?: string })
      : null,
  );
  const [startIso, setStartIso] = useState<string | null>(parsed.start);
  const [endIso, setEndIso] = useState<string | null>(parsed.end);
  const [rangeEnabled, setRangeEnabled] = useState(!!parsed.end && parsed.end !== parsed.start);
  const [textDraft, setTextDraft] = useState(parsed.start ?? '');
  const [endTextDraft, setEndTextDraft] = useState(parsed.end ?? '');
  const [pickTarget, setPickTarget] = useState<'start' | 'end'>('start');
  const [viewMonth, setViewMonth] = useState(() =>
    yearMonthFromIso(parsed.start ?? todayIso()),
  );
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const panelMode = variant === 'panel';
  const open = isOpen;

  function setRootEl(el: HTMLDivElement | null) {
    rootRef.current = el;
    setAnchorEl(el);
  }

  useLayoutEffect(() => {
    if (isOpen && rootRef.current) {
      setAnchorEl(rootRef.current);
    }
  }, [isOpen]);

  const grid = buildMonthGrid(viewMonth.year, viewMonth.month);

  useEffect(() => {
    setStartIso(parsed.start);
    setEndIso(parsed.end);
    setRangeEnabled(!!parsed.end && parsed.end !== parsed.start);
    setTextDraft(parsed.start ?? '');
    setEndTextDraft(parsed.end ?? '');
    if (parsed.start) {
      setViewMonth(yearMonthFromIso(parsed.start));
    }
  }, [property?.syncVersion, parsed.start, parsed.end]);

  useEffect(() => {
    setDisplayFormat(configFormat);
  }, [configFormat, column.id]);

  useEffect(() => {
    if (!isOpen || panelMode) {
      return;
    }
    function onOutsideClick(event: MouseEvent): void {
      const target = event.target as HTMLElement | null;
      if (target?.closest('[data-cs-date-editor="true"]')) {
        return;
      }
      onClose();
    }
    // Use click (not mousedown) so native <select> option picks fire onChange first.
    document.addEventListener('click', onOutsideClick, true);
    return () => document.removeEventListener('click', onOutsideClick, true);
  }, [isOpen, panelMode, onClose]);

  const displayText = formatDateValue(
    startIso
      ? encodeDateCellValue(startIso, rangeEnabled ? endIso : null) ?? undefined
      : null,
    displayFormat,
  );

  function applyFormat(next: DateFormat) {
    if (next === displayFormat) {
      return;
    }
    setDisplayFormat(next);
    onDateFormatChange?.(next);
  }

  async function commit(start: string | null, end: string | null, withRange: boolean) {
    const encoded = encodeDateCellValue(start, withRange ? end : null);
    await onSave(encoded as Record<string, unknown> | null);
    onClose();
  }

  function applyTextToStart() {
    const iso = parseDateInput(textDraft);
    setStartIso(iso);
    if (iso) {
      setTextDraft(iso);
      setViewMonth(yearMonthFromIso(iso));
    }
  }

  function applyTextToEnd() {
    const iso = parseDateInput(endTextDraft);
    setEndIso(iso);
    if (iso) {
      setEndTextDraft(iso);
    }
  }

  function onDayClick(iso: string) {
    if (disabled || saving) {
      return;
    }
    if (!rangeEnabled) {
      setStartIso(iso);
      setTextDraft(iso);
      setEndIso(null);
      setEndTextDraft('');
      void commit(iso, null, false);
      return;
    }
    if (pickTarget === 'start') {
      setStartIso(iso);
      setTextDraft(iso);
      setPickTarget('end');
      return;
    }
    setEndIso(iso);
    setEndTextDraft(iso);
    void commit(startIso ?? iso, iso, true);
  }

  function inRange(iso: string | null): boolean {
    if (!iso || !startIso || !rangeEnabled) {
      return false;
    }
    const end = endIso ?? startIso;
    const a = startIso < end ? startIso : end;
    const b = startIso < end ? end : startIso;
    return iso >= a && iso <= b;
  }

  function toggleOpen() {
    if (disabled || saving) {
      return;
    }
    if (isOpen) {
      onClose();
    } else {
      onOpen();
    }
  }

  const popoverBody = open
    ? [
        createElement(
          'div',
          {
            className: rangeEnabled
              ? 'cs-db-date-fields cs-db-date-fields-range'
              : 'cs-db-date-fields',
          },
          createElement('input', {
            type: 'text',
            className: 'cs-db-date-input',
            value: textDraft,
            placeholder: 'YYYY-MM-DD',
            disabled: disabled || saving,
            onFocus: () => setPickTarget('start'),
            onChange: (e: Event) => setTextDraft((e.target as HTMLInputElement).value),
            onBlur: applyTextToStart,
            onKeyDown: (e: KeyboardEvent) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                applyTextToStart();
                const iso = parseDateInput(textDraft);
                if (iso && !rangeEnabled) {
                  void commit(iso, null, false);
                }
              }
              e.stopPropagation();
            },
          }),
          rangeEnabled
            ? createElement('input', {
                type: 'text',
                className: 'cs-db-date-input',
                value: endTextDraft,
                placeholder: 'End date',
                disabled: disabled || saving,
                onChange: (e: Event) =>
                  setEndTextDraft((e.target as HTMLInputElement).value),
                onBlur: applyTextToEnd,
                onFocus: () => setPickTarget('end'),
                onKeyDown: (e: KeyboardEvent) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applyTextToEnd();
                    void commit(startIso, parseDateInput(endTextDraft), true);
                  }
                  e.stopPropagation();
                },
              })
            : null,
        ),
        createElement(
          'div',
          { className: 'cs-db-date-cal-header' },
          createElement(
            'button',
            {
              type: 'button',
              className: 'cs-db-date-cal-nav',
              disabled: disabled || saving,
              onClick: () =>
                setViewMonth((v) => shiftMonth(v.year, v.month, -1)),
            },
            '‹',
          ),
          createElement(
            'span',
            { className: 'cs-db-date-cal-title' },
            monthLabel(viewMonth.year, viewMonth.month),
          ),
          createElement(
            'button',
            {
              type: 'button',
              className: 'cs-db-date-cal-nav',
              disabled: disabled || saving,
              onClick: () =>
                setViewMonth((v) => shiftMonth(v.year, v.month, 1)),
            },
            '›',
          ),
        ),
        createElement(
          'div',
          { className: 'cs-db-date-cal-grid' },
          ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((d) =>
            createElement('span', { key: d, className: 'cs-db-date-cal-dow' }, d),
          ),
          ...grid.map((cell, i) => {
            if (!cell.iso) {
              return createElement('span', {
                key: `pad-${i}`,
                className: 'cs-db-date-cal-day cs-db-date-cal-day-pad',
              });
            }
            const selected = cell.iso === startIso || cell.iso === endIso;
            const ranged = inRange(cell.iso);
            return createElement(
              'button',
              {
                key: cell.iso,
                type: 'button',
                className: [
                  'cs-db-date-cal-day',
                  selected ? 'cs-db-date-cal-day-selected' : '',
                  ranged ? 'cs-db-date-cal-day-in-range' : '',
                ]
                  .filter(Boolean)
                  .join(' '),
                disabled: disabled || saving,
                onClick: () => onDayClick(cell.iso!),
              },
              String(cell.day),
            );
          }),
        ),
        createElement(
          'label',
          { className: 'cs-db-date-toggle' },
          createElement('input', {
            type: 'checkbox',
            checked: rangeEnabled,
            disabled: disabled || saving,
            onChange: (e: Event) => {
              const on = (e.target as HTMLInputElement).checked;
              setRangeEnabled(on);
              if (!on) {
                setEndIso(null);
                setEndTextDraft('');
                setPickTarget('start');
              } else {
                setPickTarget('end');
              }
            },
          }),
          'End date',
        ),
        createElement(
          'div',
          { className: 'cs-db-date-footer' },
          createElement(
            'select',
            {
              className: 'cs-db-date-format-select',
              value: displayFormat,
              disabled: disabled || saving || !onDateFormatChange,
              onChange: (e: Event) =>
                applyFormat((e.target as HTMLSelectElement).value as DateFormat),
              onMouseDown: (e: MouseEvent) => e.stopPropagation(),
            },
            DATE_FORMATS.map((f) =>
              createElement('option', { key: f, value: f }, DATE_FORMAT_LABELS[f]),
            ),
          ),
          createElement(
            'button',
            {
              type: 'button',
              className: 'cs-db-date-clear',
              disabled: disabled || saving,
              onClick: () => void commit(null, null, false),
            },
            'Clear',
          ),
        ),
      ]
    : null;

  const popoverInner =
    popoverBody && createElement('div', { className: 'cs-db-date-popover-inner' }, ...popoverBody);

  const popover =
    panelMode && isOpen && popoverInner
      ? createElement('div', { className: 'cs-db-date-popover' }, popoverInner)
      : createElement(CellPopoverPortal, {
          open: isOpen,
          anchorEl,
          dataAttribute: 'cs-date-editor',
          width: 280,
          heightEstimate: 400,
          className: 'cs-db-date-popover',
          children: popoverInner,
        });

  if (panelMode) {
    return createElement(
      'div',
      {
        ref: setRootEl,
        'data-cs-date-editor': 'true',
        className: 'cs-db-date-editor-root cs-db-date-editor-panel',
      },
      createElement(
        'button',
        {
          type: 'button',
          className: 'cs-db-row-panel-value-trigger',
          disabled: disabled || saving,
          onClick: (e: MouseEvent) => {
            e.stopPropagation();
            toggleOpen();
          },
          onMouseDown: (e: MouseEvent) => e.stopPropagation(),
        },
        displayText
          ? createElement('span', { className: 'cs-db-date-display' }, displayText)
          : createElement('span', { className: 'cs-db-select-placeholder' }, 'Select date…'),
      ),
      isOpen ? popover : null,
    );
  }

  return createElement(
    'div',
    { ref: setRootEl, 'data-cs-date-editor': 'true', className: 'cs-db-date-editor-root' },
    createElement(
      Pressable,
      {
        onPress: toggleOpen,
        className:
          'flex h-full min-h-[32px] w-full min-w-0 max-w-full overflow-hidden items-center justify-center px-2 py-1',
      },
      displayText
        ? createElement(
            Text,
            { className: 'cs-db-date-display min-w-0 max-w-full text-sm text-fg-muted' },
            displayText,
          )
        : null,
    ),
    popover,
  );
}
